/**
 * Types for Content Intelligence module.
 */

export interface CiWorkspace {
  id: number
  name: string
  type: 'brand' | 'personal'
  status: 'active' | 'archived'
  daily_token_limit: number
  ai_tokens_today: number
  auto_approve: boolean
  notif_staging: boolean
  timezone: string
  created_at: string
}

export interface CiContext {
  id: number
  workspace_id: number
  compressed: string | null
  token_count: number | null
  file_name: string | null
  updated_at: string
}

export interface CiContextEntry {
  id: number
  entry_type: 'achievement' | 'metric' | 'case' | 'quote'
  content: string
  tags: string | null
  entry_date: string | null
  is_archived: boolean
  created_at: string
}

export interface CiSource {
  id: number
  name: string
  platform: 'telegram' | 'twitter' | 'linkedin'
  external_id: string | null
  status: string
  min_views: number
  min_reactions: number
  last_parsed_at: string | null
  posts_7d: number
  created_at: string
}

export interface CiCollectedPost {
  id: number
  source_id: number
  external_id: string
  text: string | null
  media_only: boolean
  views: number
  reactions: number
  posted_at: string | null
  collected_at: string
  topic: string | null
  topic_tags: string | null
  ai_score: number | null
  ai_summary: string | null
  analysis_status: string
  skip_reason: string | null
}

export interface CiOutputChannel {
  id: number
  workspace_id: number
  platform: 'telegram' | 'linkedin'
  name: string
  external_id: string | null
  token_status: string
  lang: string
  tone: string
  perspective: string
  tov_prompt: string | null
  schedule_slots: string | null
  posts_per_week: number
  max_posts_day: number
  posts_today: number
  status: string
  created_at: string
}

export interface CiGeneratedPost {
  id: number
  workspace_id: number
  output_channel_id: number
  source_post_id: number
  text: string
  text_edited: string | null
  context_stale: boolean
  status: string
  reject_reason: string | null
  user_comment: string | null
  deferred_until: string | null
  generation_source: 'reactive' | 'planner'
  published_at: string | null
  platform_post_id: string | null
  ai_model: string | null
  scheduled_for: string | null
  created_at: string
}

export interface CiPlannerStatus {
  channels: Array<{
    channel_id: number
    channel_name: string
    platform: string
    posts_per_week: number
    pipeline_count: number
    deficit: number
  }>
}

export interface CiWorkspaceStats {
  collected: number
  analyzed: number
  passed_filter: number
  generated: number
  approved: number
  published: number
  ai_tokens_today: number
  ai_cost_today_usd: number
}

export interface CiAiUsage {
  tokens_total: number
  tokens_in: number
  tokens_out: number
  cost_usd: number
  breakdown: Record<string, { tokens: number; cost_usd: number }>
}

// Helpers

export function getPlatformIcon(platform: string): string {
  const icons: Record<string, string> = {
    telegram: '📨',
    twitter: '🐦',
    linkedin: '💼',
  }
  return icons[platform] || '🌐'
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-700',
    pending_approval: 'bg-yellow-100 text-yellow-700',
    analyzed: 'bg-blue-100 text-blue-700',
    approved: 'bg-green-100 text-green-700',
    published: 'bg-green-200 text-green-800',
    rejected: 'bg-red-100 text-red-700',
    failed: 'bg-red-200 text-red-800',
    skipped: 'bg-gray-100 text-gray-600',
    auth_required: 'bg-orange-100 text-orange-700',
    active: 'bg-green-100 text-green-700',
    paused: 'bg-gray-100 text-gray-600',
  }
  return colors[status] || 'bg-gray-100 text-gray-600'
}
